/**
 * @file    useDataTableProps
 * @scope   SHARED
 * @summary Builds the react-data-table props (pagination, sorting, styles) of the legacy table
 *          and owns the sort-icon direction.
 * @consumes sorting utils, DynamicTableOld.style, theme
 * @produces { dataTableProps, sortDirection }
 * @sideEffects Server pagination / sorting call getData.
 */
import { Dispatch, MutableRefObject, SetStateAction, useMemo, useState } from 'react';
import { SortOrder, TableColumn } from 'react-data-table-component';
import { TableState } from 'components/DynamicTable/common/types';
import { createSortFunction } from '../utils/sorting';
import { cellLink, customStyles, highlightStyles } from '../styles/DynamicTableOld.style';
import { STRIPED_THEME } from '../styles/theme';

interface UseDataTablePropsArgs {
  table?: TableState<TableColumn<any>>;
  setTable: Dispatch<SetStateAction<TableState<TableColumn<any>> | undefined>>;
  tableOptions: any;
  hasIdSurveyPanel: boolean;
  isTableReady: boolean;
  firstRender: MutableRefObject<boolean>;
  totalsFirstRow: MutableRefObject<any>;
  getData: (page: number, perPage?: number, column?: any, direction?: string) => Promise<void>;
}

const buildCustomStyles = (showTotals: boolean, hasIdSurveyPanel: boolean) => ({
  ...customStyles,
  ...(showTotals ? { rows: { style: { ...customStyles.rows.style, ...highlightStyles } } } : {}),
  cells: {
    style: hasIdSurveyPanel
      ? { ...customStyles.cells.style, ...cellLink }
      : { ...customStyles.cells.style },
  },
  responsiveWrapper: { style: { height: '100%' } },
});

export const useDataTableProps = ({
  table,
  setTable,
  tableOptions,
  hasIdSurveyPanel,
  isTableReady,
  firstRender,
  totalsFirstRow,
  getData,
}: UseDataTablePropsArgs) => {
  const { striped, pagination, paginationServer, showTotals, sortServer } = tableOptions || {};
  const [sortDirection, setSortDirection] = useState<string>('desc');

  const handlePageChange = (page: number) => {
    if (page !== 1) {
      firstRender.current = false;
      getData(page, table?.paginationPerPage);
    }
  };

  const handlePerRowsChange = async (perPage: number, page: number) => {
    if (!table) return;
    setTable({ ...table, paginationPerPage: perPage });
    if (page !== 1) {
      firstRender.current = false;
      getData(page, perPage);
    }
  };

  /** Fires only when sorting is managed server side. */
  const handleServerSort = (column: TableColumn<any>, direction: SortOrder) => {
    setSortDirection(direction);
    getData(1, table?.paginationPerPage, column.sortField, direction);
  };

  const handleSortIcon = (_column: TableColumn<any>, direction: SortOrder) => {
    setSortDirection(direction);
  };

  // QUIRK: memoized on the data only; handlers keep the closure of the render that built it.
  const dataTableProps = useMemo(() => {
    if (!isTableReady || !table) return {};
    return {
      ...(pagination
        ? {
            pagination,
            paginationTotalRows: table.paginationTotalRows,
            paginationPerPage: table.paginationPerPage,
          }
        : {}),
      onChangeRowsPerPage: handlePerRowsChange,
      onChangePage: handlePageChange,
      ...(paginationServer ? { paginationPerPage: table?.paginationPerPage } : {}),
      ...(striped ? { striped, theme: STRIPED_THEME } : {}),
      ...(sortServer ? { onSort: handleServerSort, sortServer } : {}),
      // WHY: overrides the server `onSort` above; kept from the original implementation.
      sortFunction: createSortFunction(table?.sortingType, showTotals, totalsFirstRow),
      onSort: handleSortIcon,
      customStyles: buildCustomStyles(showTotals, hasIdSurveyPanel),
      defaultSortFieldId: 1,
    };
  }, [table?.data]);

  return { dataTableProps, sortDirection };
};
